/* React hooks */
import {useEffect, useRef, useState} from "react";
import {updateDeceasedAdministrator} from "../../api/pro/deceasedReq";
import useAuth from "../../hooks/useAuth";
import {useLocation, useParams} from "react-router-dom";
import useDeceased from "../../hooks/useDeceased";
import LastNameForm from "../../components/common/input-layout/LastNameForm";
import FirstnameForm from "../../components/common/input-layout/FirstnameForm";
import CivilityForm from "../../components/common/input-layout/CivilityForm";
import InputFullAddress from "../../components/common/input-layout/InputFullAddress";
import {checkRequiredFields, getErrorListFromValidation, removeErrorList, validateFormInput, capitalize, upperCaseName, ucfirst, patternListChoices} from "../../helper/tools";
import useData from "../../hooks/useData";


export default function FormAdminChoice({
    legend,
    deceasedInfos,
    apiDeceasedInfos,
    apiAdminInfos,
    apiChildrenInfos,
    childrenInfos,
    adminInfos,
    setShowModal,
    registerSubmitFunction,
    onIsConjointAdminChange,
    onAdminFilledChange,
    liveMaritalStatus,
    noSubmitButton = false,
}) {
    const { auth } = useAuth();
    const token = auth?.token;
    const { id } = useParams();
    const [values, setValues] = useState({});
    const [isFormModified, setIsFormModified] = useState(false);
    const fieldsetRef = useRef();
    const affiliationRef = useRef();
    const [ selectedChildId, setSelectedChildId ] = useState();
    const [ selectedChild, setSelectedChild ] = useState();
    const [ newAdministrator, setNewAdministrator ] = useState();
    const { affiliationList, partnerInfos } = useDeceased();
    // Dans le wizard, le statut marital peut être modifié à l'étape 1 sans avoir encore été
    // enregistré/rechargé (deceasedInfos reste donc l'ancienne valeur) : se baser en priorité sur
    // liveMaritalStatus, remonté en direct par FormFamilyStatusInfos.
    const effectiveMaritalStatus = liveMaritalStatus !== undefined ? liveMaritalStatus : deceasedInfos?.maritalStatus;
    // Veuf(ve)/Divorcé(e)/Séparé(e) : ne pas poser la question du conjoint, même si un conjoint
    // existe encore dans les infos du défunt (ex: renseigné avant le veuvage/divorce/séparation).
    const showConjointQuestion = !!partnerInfos?.id
        && effectiveMaritalStatus != 2
        && effectiveMaritalStatus != 3
        && effectiveMaritalStatus != 7;
    const [ adultChildrenList, setAdultChildrenList ] = useState();
    const [ defaultChildId, setDefaultChildId ] = useState();
    const { selectedCity } = useData();
    const isInitialized = useRef(false);
    const isNewAdminInitialized = useRef(false);
    const isChildAdminInitialized = useRef(false);
    const childFieldsEditedRef = useRef(false);
    const handleSubmitRef = useRef();

    useEffect( () => {
        if (selectedCity?.zip && selectedCity?.name) {
            setNewAdministrator(prev => {
                return {
                    ...prev,
                    address: {
                        ...prev?.address,
                        zipcode: selectedCity?.zip,
                        city: selectedCity?.name
                    }
                };
            })

            setSelectedChild(prev => {
                return {
                    ...prev,
                    address: {
                        ...prev?.address,
                        zipcode: selectedCity?.zip,
                        city: selectedCity?.name
                    }
                };
            })
        }
    }, [selectedCity])

    const onChangeSelectedChild = (e) => {
        setIsFormModified(true);
        childFieldsEditedRef.current = true;
        const fieldValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setSelectedChild(prev => ({...prev, [e.target.name] : fieldValue }))
        validateFormInput(e)
    };

    const onChangeChildAdmin = (e) => {
        setIsFormModified(true);
        // Un nouvel enfant est choisi : on autorise de nouveau la resynchronisation automatique
        // depuis childrenInfos (cf effet plus bas), y compris si cet enfant avait déjà été
        // sélectionné une première fois avec des données alors incomplètes (ex: juste créé).
        childFieldsEditedRef.current = false;
        const newChildId = e.target.value;
        setSelectedChildId(newChildId);
        // Préremplissage immédiat (sans attendre le prochain cycle de rendu), pour éviter tout
        // flash de champs Civilité/Nom/Prénom vides le temps que l'effet de resynchronisation
        // ci-dessous se déclenche.
        const childData = childrenInfos?.find(child => newChildId == child?.id);
        if (childData) {
            setSelectedChild(childData);
        }
        validateFormInput(e)
    };

    const onChangeValues = (e) => {
        setIsFormModified(true);
        let value = e.target.value;

        if (e.target.type === "radio" && (e.target.name === "isConjointAdmin" || e.target.name === "isChildAdmin")) {
            value = parseInt(value, 10);
        }

        setValues(prevValues => ({...prevValues, [e.target.name] : value }))
        if (e.target.type === "checkbox") {
            setValues(prevValues => ({...prevValues, [e.target.name] : e.target.checked }))
        }
        validateFormInput(e)
    }

    const onChangeChildAddress = (e) => {
        setIsFormModified(true);
        childFieldsEditedRef.current = true;
        const fieldValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;

        setSelectedChild(prev => ({
            ...prev,
            address: {
                ...(prev?.address || {}),
                [e.target.name]: fieldValue
            }
        }));

        validateFormInput(e)
    }

    const onChangeChildSameAddressAsDeceased = (e) => {
        const checked = e.target.checked;
        setIsFormModified(true);
        childFieldsEditedRef.current = true;
        setSelectedChild(prev => ({
            ...prev,
            sameAddressAsDeceased: checked,
            address: checked
                ? {
                    addressId: prev?.address?.addressId || 0,
                    address: deceasedInfos?.actualplace?.address,
                    addressComp: deceasedInfos?.actualplace?.addressComp,
                    zipcode: deceasedInfos?.actualplace?.zipcode,
                    city: deceasedInfos?.actualplace?.city,
                    country: deceasedInfos?.actualplace?.country || "FR"
                }
                : prev?.address
        }));
    }

    const onChangeNewAdmin = (e) => {
        setIsFormModified(true);
        let fieldValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        if (e.target.name === "familyAffiliation") {
            fieldValue = parseInt(fieldValue, 10);
        }
        setNewAdministrator(prev => ({...prev, [e.target.name] : fieldValue }))
        validateFormInput(e)
    }

    const onChangeNewAdminAddress = (e) => {
        setIsFormModified(true);
        const fieldValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;

        setNewAdministrator(prev => ({
            ...prev,
            address: {
                ...(prev?.address || {}),
                [e.target.name]: fieldValue
            }
        }));

        validateFormInput(e)
    }

    const onChangeNewAdminSameAddressAsDeceased = (e) => {
        const checked = e.target.checked;
        setIsFormModified(true);
        setNewAdministrator(prev => ({
            ...prev,
            sameAddressAsDeceased: checked,
            address: checked
                ? {
                    addressId: prev?.address?.addressId || 0,
                    address: deceasedInfos?.actualplace?.address,
                    addressComp: deceasedInfos?.actualplace?.addressComp,
                    zipcode: deceasedInfos?.actualplace?.zipcode,
                    city: deceasedInfos?.actualplace?.city,
                    country: deceasedInfos?.actualplace?.country || "FR"
                }
                : prev?.address
        }));
    }

    useEffect(() => {
        // Les champs adresse peuvent avoir été marqués "invalid" lors d'une précédente tentative de
        // validation ; le pré-remplissage automatique via une case "adresse identique" ne déclenche
        // aucun événement sur ces champs (désactivés), donc cette classe ne se retire pas d'elle-même.
        if ((selectedChild?.sameAddressAsDeceased || newAdministrator?.sameAddressAsDeceased) && fieldsetRef.current) {
            const addressFields = fieldsetRef.current.querySelectorAll('[name="address"], [name="addressComp"], [name="zipcode"], [name="city"], [name="country"]');
            addressFields.forEach(field => {
                field.parentElement.classList.remove("invalid");
                field.parentElement.classList.add("valid");
            });
        }
    }, [ selectedChild?.sameAddressAsDeceased, selectedChild?.address, newAdministrator?.sameAddressAsDeceased, newAdministrator?.address ]);

    useEffect(() => {
        if (onIsConjointAdminChange) {
            onIsConjointAdminChange(values?.isConjointAdmin);
        }
    }, [ values?.isConjointAdmin ]);

    // Remonte au wizard si un gestionnaire est effectivement renseigné (conjoint, enfant
    // sélectionné, ou "autre" avec civilité/nom/prénom saisis) : la question "l'adresse de
    // correspondance est-elle celle du gestionnaire ?" dans FormCorrespondentChoice n'a de sens
    // que si un gestionnaire a réellement été choisi.
    useEffect(() => {
        if (!onAdminFilledChange) {
            return;
        }
        const isAdminFilled = values?.isConjointAdmin === 1
            ? true
            : values?.isChildAdmin === 1
                ? !!selectedChildId
                : (values?.isConjointAdmin === 0 && (values?.isChildAdmin === 0 || values?.isChildAdmin === null))
                    ? !!(newAdministrator?.civility && newAdministrator?.lastName && newAdministrator?.firstName)
                    : false;
        onAdminFilledChange(isAdminFilled);
    }, [ values?.isConjointAdmin, values?.isChildAdmin, selectedChildId, newAdministrator?.civility, newAdministrator?.lastName, newAdministrator?.firstName ]);

    useEffect(() => {
        setDefaultChildId(childrenInfos?.find(child => child?.isAdministrator)?.id)
    }, [ childrenInfos ]);

    useEffect(() => {
        setAdultChildrenList(childrenInfos?.filter(child => child?.isMinor == 0 && child?.isDeceased == 0))
    }, [ childrenInfos ]);

    useEffect(() => {
        // N'initialiser les valeurs QU'UNE SEULE FOIS au montage, une fois childrenInfos ET
        // partnerInfos chargés (les 4 appels API du wizard partent en parallèle : sans attendre
        // partnerInfos, showConjointQuestion peut encore valoir false à ce moment-là et verrouiller
        // "Non" par défaut au lieu de "Oui" alors qu'un conjoint existe bel et bien).
        if (!isInitialized.current && (adminInfos || deceasedInfos) && childrenInfos !== null && partnerInfos !== undefined) {
            const sourceIsConjointAdmin = adminInfos?.isConjointAdmin !== undefined
                ? adminInfos.isConjointAdmin
                : deceasedInfos?.isConjointAdmin;

            const sourceIsChildAdmin = adminInfos?.isChildAdmin !== undefined
                ? adminInfos.isChildAdmin
                : deceasedInfos?.isChildAdmin;

            const hasAdultChildren = childrenInfos?.some(child => child?.isMinor == 0 && child?.isDeceased == 0);

            // Pas de valeur existante : cocher "Oui" par défaut pour le conjoint si la question
            // du conjoint est affichée (showConjointQuestion), sinon "Oui" pour les enfants si
            // cette question-là s'affiche à la place (enfants adultes disponibles), sinon "Non"
            // (déjà traité comme équivalent à 0 par le reste du composant, cf lignes 413/490).
            // Si la question n'est plus posée (ex: changement de statut marital vers veuf/divorcé/
            // séparé), ignorer une éventuelle ancienne valeur enregistrée : sinon isConjointAdmin
            // reste bloqué à 1 et masque à tort les propositions "enfant"/"autre" qui en dépendent.
            const parsedIsConjointAdmin = !showConjointQuestion
                ? 0
                : (sourceIsConjointAdmin !== null && sourceIsConjointAdmin !== undefined
                    ? (typeof sourceIsConjointAdmin === 'boolean'
                        ? (sourceIsConjointAdmin ? 1 : 0)
                        : parseInt(sourceIsConjointAdmin, 10))
                    : 1);

            // Si la question "enfant" n'est plus posée (plus aucun enfant adulte, ex: enfant
            // supprimé/devenu mineur depuis l'enregistrement), ignorer une éventuelle ancienne
            // valeur enregistrée : sinon isChildAdmin reste bloqué à 1 et masque à tort la
            // proposition "autre", alors que ni conjoint ni enfant ne peuvent plus être proposés.
            const parsedIsChildAdmin = !hasAdultChildren
                ? 0
                : (sourceIsChildAdmin !== null && sourceIsChildAdmin !== undefined
                    ? (typeof sourceIsChildAdmin === 'boolean'
                        ? (sourceIsChildAdmin ? 1 : 0)
                        : parseInt(sourceIsChildAdmin, 10))
                    : (!showConjointQuestion ? 1 : null));

            setValues({
                isConjointAdmin: parsedIsConjointAdmin,
                isChildAdmin: parsedIsChildAdmin
            });

            isInitialized.current = true;
        }
    }, [ adminInfos, deceasedInfos, childrenInfos, showConjointQuestion ]);

    // Si le statut marital change EN DIRECT après l'initialisation (ex: passage à veuf/divorcé/
    // séparé dans l'étape 1 du wizard alors que isConjointAdmin valait déjà 1), la question du
    // conjoint disparaît mais values.isConjointAdmin resterait sinon bloqué à 1, masquant à tort
    // les propositions "enfant"/"autre" qui en dépendent.
    useEffect(() => {
        if (isInitialized.current && !showConjointQuestion) {
            setValues(prev => prev?.isConjointAdmin ? { ...prev, isConjointAdmin: 0 } : prev);
        }
    }, [ showConjointQuestion ]);

    // Cas inverse : l'initialisation ci-dessus s'est exécutée avant que partnerInfos ait fini de
    // charger (son .id pas encore disponible), verrouillant isConjointAdmin à 0 par défaut alors
    // qu'un conjoint existe bel et bien. Une fois partnerInfos chargé et showConjointQuestion
    // devenu vrai, remonter à 1 — mais seulement s'il n'y a aucune valeur explicitement
    // enregistrée (sinon on écraserait un "Non" sciemment choisi par l'admin).
    useEffect(() => {
        if (isInitialized.current && showConjointQuestion) {
            const sourceIsConjointAdmin = adminInfos?.isConjointAdmin !== undefined
                ? adminInfos.isConjointAdmin
                : deceasedInfos?.isConjointAdmin;
            if (sourceIsConjointAdmin === null || sourceIsConjointAdmin === undefined) {
                setValues(prev => prev?.isConjointAdmin === 1 ? prev : { ...prev, isConjointAdmin: 1 });
            }
        }
    }, [ showConjointQuestion ]);

    // Même principe si le dernier enfant adulte disparaît EN DIRECT après l'initialisation (ex:
    // suppression d'un enfant alors qu'il était déjà sélectionné comme gestionnaire) : sans ça,
    // isChildAdmin resterait bloqué à 1 et la proposition "autre" ne s'afficherait jamais alors
    // que ni conjoint ni enfant ne peuvent plus être proposés.
    useEffect(() => {
        if (isInitialized.current && adultChildrenList && adultChildrenList.length === 0) {
            setValues(prev => prev?.isChildAdmin ? { ...prev, isChildAdmin: 0 } : prev);
        }
    }, [ adultChildrenList ]);

    // Initialiser newAdministrator QU'UNE SEULE FOIS en basculant vers "autre administrateur" :
    // un rechargement d'adminInfos suite à l'enregistrement d'un autre sous-formulaire ne doit pas écraser une saisie en cours.
    useEffect(() => {
        if (adminInfos && !isNewAdminInitialized.current && (values?.isConjointAdmin === 0 || values?.isConjointAdmin === null) &&
            (values?.isChildAdmin === 0 || values?.isChildAdmin === null)) {
            setNewAdministrator({
                ...adminInfos,
                // Présélectionner le lien de parenté seulement s'il est réellement renseigné en base.
                familyAffiliation: adminInfos?.familyAffiliation || "",
                address: adminInfos?.address || {}
            });
            isNewAdminInitialized.current = true;
        }
    }, [ adminInfos, values?.isConjointAdmin, values?.isChildAdmin ]);



    const handleSubmit = async (e) => {
        e.preventDefault()

        // Utiliser fieldsetRef pour la validation
        const formElement = fieldsetRef.current || e.target;
        const requiredFieldsValid = checkRequiredFields(formElement);

        // Groupes de radios "Le gestionnaire est-il le conjoint ?" / "...un des enfants ?" : seul
        // le radio "Oui" porte l'attribut required, ce qui ne bloque pas de façon fiable la
        // validation native quand aucun des deux boutons n'est coché. Vérification explicite en
        // plus de checkRequiredFields.
        const isConjointFieldMissing = showConjointQuestion && values?.isConjointAdmin == null;
        if (isConjointFieldMissing) {
            formElement?.querySelector('input[name="isConjointAdmin"]')?.parentElement?.classList.add("invalid");
        }
        const isChildAdminFieldMissing = adultChildrenList?.length > 0 &&
            (values?.isConjointAdmin == 0 || values?.isConjointAdmin == null) && values?.isChildAdmin == null;
        if (isChildAdminFieldMissing) {
            formElement?.querySelector('input[name="isChildAdmin"]')?.parentElement?.classList.add("invalid");
        }

        if (!requiredFieldsValid || isConjointFieldMissing || isChildAdminFieldMissing) {
            return;
        }
        let body;
        if (values?.isConjointAdmin == 1) {
            body = {
                deceasedId: id,
                isConjointAdmin: 1,
                isChildAdmin: 0,
                adminId: partnerInfos?.id,
                civility: partnerInfos?.civility,
                lastName: partnerInfos?.lastName,
                firstName: partnerInfos?.firstName,
                birthName: partnerInfos?.birthName,
                address: {
                    addressId: partnerInfos?.address?.addressId || partnerInfos?.actualplace?.addressId || 0,
                    address: partnerInfos?.address?.address || partnerInfos?.actualplace?.address,
                    addressComp: partnerInfos?.address?.addressComp || partnerInfos?.actualplace?.addressComp,
                    zipcode: partnerInfos?.address?.zipcode || partnerInfos?.actualplace?.zipcode,
                    city: partnerInfos?.address?.city || partnerInfos?.actualplace?.city,
                    country: partnerInfos?.address?.country || partnerInfos?.actualplace?.country || "FR"
                },
                phone: partnerInfos?.phone,
                mobile: partnerInfos?.mobile,
                email: partnerInfos?.email,
                familyAffiliation: 1,
            }

        } else if (values?.isChildAdmin == 1) {
            body = {
                familyAffiliation: 5,
                deceasedId: id,
                isConjointAdmin: 0,
                isChildAdmin: 1,
                childAdminId: selectedChildId,
                civility: selectedChild?.civility,
                lastName: selectedChild?.lastName,
                firstName: selectedChild?.firstName,
                birthName: selectedChild?.birthName,
                address: {
                    addressId: selectedChild?.address?.addressId || 0,
                    address: selectedChild?.address?.address,
                    addressComp: selectedChild?.address?.addressComp,
                    zipcode: selectedChild?.address?.zipcode,
                    city: selectedChild?.address?.city,
                    country: selectedChild?.address?.country,
                },
                phone: selectedChild?.phone,
                mobile: selectedChild?.mobile,
                email: selectedChild?.email,
            }

        } else {
            body = {
                deceasedId: id,
                isConjointAdmin: 0,
                isChildAdmin: 0,
                adminId: newAdministrator?.id || adminInfos?.id,
                civility: newAdministrator?.civility,
                lastName: newAdministrator?.lastName,
                firstName: newAdministrator?.firstName,
                birthName: newAdministrator?.birthName,
                address: {
                    addressId: newAdministrator?.address?.addressId || 0,
                    address: newAdministrator?.address?.address,
                    addressComp: newAdministrator?.address?.addressComp,
                    zipcode: newAdministrator?.address?.zipcode,
                    city: newAdministrator?.address?.city,
                    country: newAdministrator?.address?.country || "FR"
                },
                phone: newAdministrator?.phone,
                mobile: newAdministrator?.mobile,
                email: newAdministrator?.email,
                familyAffiliation: newAdministrator?.familyAffiliation
            }
        }
        await updateDeceasedAdministrator(token, body)
            .then((response) => {
                removeErrorList();
                if (!response?.response?.data?.error) {
                    setIsFormModified(false);

                    // Afficher le message de succès dans le bon fieldset
                    const errorDiv = fieldsetRef.current?.querySelector('div.errors');
                    if (errorDiv) {
                        errorDiv.insertAdjacentHTML('beforeend', '<p class="success-message">Les modifications ont été enregistrées avec succès !</p>');

                        // Supprimer le message après 3 secondes
                        setTimeout(() => {
                            const successMsg = errorDiv.querySelector('.success-message');
                            if (successMsg) {
                                successMsg.remove();
                            }
                        }, 3000);
                    }

                    apiDeceasedInfos();
                    apiAdminInfos();

                    if (values?.isChildAdmin == 1 && apiChildrenInfos) {
                        apiChildrenInfos();
                    }

                    if (!noSubmitButton && setShowModal) {
                        setShowModal(false);
                    }
                } else {
                    getErrorListFromValidation(response?.response?.data?.error)
                }
            })
    }

    useEffect(() => {
        // Préselectionne l'enfant déjà enregistré comme gestionnaire, une seule fois au chargement
        // (le <select> est non-contrôlé : sans cela, selectedChildId resterait vide tant que
        // l'utilisateur n'a pas lui-même changé la sélection).
        if (!isChildAdminInitialized.current && defaultChildId) {
            setSelectedChildId(defaultChildId);
            isChildAdminInitialized.current = true;
        }
    }, [ defaultChildId ]);

    useEffect(() => {
        // Resynchronise tant que l'utilisateur n'a pas lui-même modifié les champs de l'enfant
        // sélectionné (childFieldsEditedRef, remis à false dès qu'un nouvel enfant est choisi) :
        // sans ça, un enfant sélectionné une première fois avec des données encore incomplètes
        // (ex: juste créé à l'étape précédente) ne se mettait plus jamais à jour, même une fois
        // childrenInfos rafraîchi avec ses informations complètes.
        if (selectedChildId && childrenInfos && !childFieldsEditedRef.current) {
            const childData = childrenInfos?.find(child => selectedChildId == child?.id);
            if (childData) {
                setSelectedChild(childData);
            }
        }
    }, [ selectedChildId, childrenInfos ]);

    // Enregistrer la fonction de soumission auprès du formulaire parent.
    // handleSubmitRef est mis à jour de façon synchrone à CHAQUE rendu (pas via un effet, qui
    // accuserait un cycle de retard) : le wrapper enregistré une seule fois ci-dessous appelle
    // toujours la version la plus à jour de handleSubmit. Sans ça, un enregistrement différé
    // (dépendant de values/selectedChild) pouvait être encore en retard d'un cycle de rendu au
    // moment où le wizard déclenche la soumission (ex: juste après avoir choisi un enfant), et
    // soumettait alors un selectedChild périmé/vide.
    handleSubmitRef.current = handleSubmit;
    useEffect(() => {
        if (registerSubmitFunction) {
            registerSubmitFunction((e) => handleSubmitRef.current(e));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const FormWrapper = noSubmitButton ? 'div' : 'form';
    const formProps = noSubmitButton ? {} : { onSubmit: handleSubmit, method: "POST", noValidate: true };

    return (
        <FormWrapper {...formProps}>
            <fieldset ref={fieldsetRef}>
                <legend>{legend}</legend>
                    { showConjointQuestion &&
                        <div className="form-input">
                            <label htmlFor="isConjointAdmin" className="required">Le gestionnaire est-il le conjoint ?</label>
                            <input type="radio" name="isConjointAdmin" value={1} id="isConjointAdmin1" required onChange={onChangeValues}
                                   checked={values?.isConjointAdmin === 1}/><label htmlFor="isConjointAdmin1">Oui</label>
                            <input type="radio" name="isConjointAdmin" value={0} id="isConjointAdmin0" onChange={onChangeValues}
                                   checked={values?.isConjointAdmin === 0}/><label htmlFor="isConjointAdmin0">Non</label>
                        </div>
                    }

                { (adultChildrenList?.length > 0 && (values?.isConjointAdmin == 0 || values?.isConjointAdmin == null)) &&
                    <>
                        <div className="form-input">
                            <label htmlFor="isChildAdmin" className="required">Le gestionnaire est-il un des enfants ?</label>
                            <input type="radio" name="isChildAdmin" id="isChildAdmin1" value={1} required onChange={onChangeValues}
                                   checked={values?.isChildAdmin === 1}/><label htmlFor="isChildAdmin1">Oui</label>
                            <input type="radio" name="isChildAdmin" id="isChildAdmin0" value={0} onChange={onChangeValues}
                                   checked={values?.isChildAdmin === 0}/><label htmlFor="isChildAdmin0">Non</label>
                        </div>
                        { (values?.isChildAdmin == 1) &&
                            <>
                                <div className="form-group">
                                    <div className="form-input">
                                        <label htmlFor="childAdmin" className="required">Gestionnaire parmi les enfants :</label>
                                        <select name="childAdmin" defaultValue={defaultChildId || ""} onChange={onChangeChildAdmin} required>
                                            <option disabled value="">Sélectionnez un enfant</option>
                                            {adultChildrenList?.map((child, index) => (
                                                <option key={index} name="childAdmin" value={child?.id}>{capitalize(child?.firstName)} {upperCaseName(child?.lastName)}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <CivilityForm required={true} value={selectedChild?.civility} onChange={onChangeSelectedChild}/>
                                </div>
                                {/* Champs saisis directement en contrôlé (plutôt que via LastNameForm/
                                    FirstnameForm, qui sont non-contrôlés en interne via defaultValue) :
                                    un remontage via key provoquait un dédoublement des champs affichés
                                    lors d'un changement rapide d'enfant sélectionné. */}
                                <div className="form-input">
                                    <label htmlFor="lastName" className="required">Nom</label>
                                    <input
                                        pattern={patternListChoices?.lastname}
                                        type="text"
                                        name="lastName"
                                        value={selectedChild?.lastName || ""}
                                        onChange={(e) => { e.target.value = e.target.value.trim().toUpperCase(); onChangeSelectedChild(e); }}
                                        required
                                    />
                                </div>
                                <div className="form-input">
                                    <label htmlFor="birthName">Nom de naissance</label>
                                    <input type="text" name="birthName"
                                           value={selectedChild?.birthName || ""} onChange={(e) => { e.target.value = e.target.value.toUpperCase(); onChangeSelectedChild(e); }}/>
                                </div>
                                <div className="form-input">
                                    <label htmlFor="firstName" className="required">Prénom</label>
                                    <input
                                        pattern={patternListChoices?.firstname}
                                        type="text"
                                        name="firstName"
                                        value={selectedChild?.firstName || ""}
                                        onChange={(e) => { e.target.value = ucfirst(e.target.value.trim()); onChangeSelectedChild(e); }}
                                        required
                                    />
                                </div>
                                <div className="form-input">
                                    <label htmlFor="childSameAddressAsDeceased">L'adresse est identique à celle du défunt</label>
                                    <input
                                        type="checkbox"
                                        name="sameAddressAsDeceased"
                                        id="childSameAddressAsDeceased"
                                        checked={!!selectedChild?.sameAddressAsDeceased}
                                        onChange={onChangeChildSameAddressAsDeceased}
                                    />
                                </div>
                                <div className="form-row">
                                    <InputFullAddress
                                        addressField="address"
                                        compField="addressComp"
                                        zipField="zipcode"
                                        cityField="city"
                                        countryField="country"
                                        address={selectedChild?.address?.address || ""}
                                        comp={selectedChild?.address?.addressComp || ""}
                                        zipcode={selectedChild?.address?.zipcode || ""}
                                        city={selectedChild?.address?.city || ""}
                                        country={selectedChild?.address?.country || "FR"}
                                        onChange={onChangeChildAddress}
                                        required={!selectedChild?.sameAddressAsDeceased}
                                        disabled={!!selectedChild?.sameAddressAsDeceased}
                                        type="actual"
                                    />
                                </div>
                                <div className="form-input">
                                    <label htmlFor="phone">Téléphone fixe</label>
                                    <input type="text" name="phone" value={selectedChild?.phone || ""}
                                           onChange={onChangeSelectedChild}/>
                                </div>
                                <div className="form-input">
                                    <label htmlFor="mobile">Téléphone mobile</label>
                                    <input type="text" name="mobile" value={selectedChild?.mobile || ""}
                                           onChange={onChangeSelectedChild}/>
                                </div>
                                <div className="form-input">
                                    <label htmlFor="email">Email</label>
                                    <input type="email" name="email" value={selectedChild?.email || ""}
                                           onChange={onChangeSelectedChild}/>
                                </div>
                            </>
                        }
                    </>
                }
                { (values?.isConjointAdmin == 0 || values?.isConjointAdmin == null) && (values?.isChildAdmin == 0 || values?.isChildAdmin == null) &&
                    <>
                        <div className="form-group">
                            <CivilityForm required={true} value={newAdministrator?.civility || ""} onChange={onChangeNewAdmin}/>
                        </div>
                        <LastNameForm required={true} value={newAdministrator?.lastName || ""} onChange={onChangeNewAdmin} />
                        <div className="form-input">
                            <label htmlFor="birthName">Nom de naissance</label>
                            <input type="text" name="birthName" value={newAdministrator?.birthName || ""}
                                   onChange={(e) => { e.target.value = e.target.value.toUpperCase(); onChangeNewAdmin(e); }}/>
                        </div>
                        <FirstnameForm required={true} value={newAdministrator?.firstName || ""} onChange={onChangeNewAdmin} />
                        <div className="form-input">
                            <label htmlFor="newAdminSameAddressAsDeceased">L'adresse est identique à celle du défunt</label>
                            <input
                                type="checkbox"
                                name="sameAddressAsDeceased"
                                id="newAdminSameAddressAsDeceased"
                                checked={!!newAdministrator?.sameAddressAsDeceased}
                                onChange={onChangeNewAdminSameAddressAsDeceased}
                            />
                        </div>
                        <div className="form-row">
                            <InputFullAddress
                                addressField="address"
                                compField="addressComp"
                                zipField="zipcode"
                                cityField="city"
                                countryField="country"
                                address={newAdministrator?.address?.address || ""}
                                comp={newAdministrator?.address?.addressComp || ""}
                                zipcode={newAdministrator?.address?.zipcode || ""}
                                city={newAdministrator?.address?.city || ""}
                                country={newAdministrator?.address?.country || "FR"}
                                onChange={onChangeNewAdminAddress}
                                required={!newAdministrator?.sameAddressAsDeceased}
                                disabled={!!newAdministrator?.sameAddressAsDeceased}
                                type="actual"
                            />
                        </div>
                        <div className="form-input">
                            <label htmlFor="phone">Téléphone fixe</label>
                            <input type="text" name="phone" value={newAdministrator?.phone || ""}
                                   onChange={onChangeNewAdmin}/>
                        </div>
                        <div className="form-input">
                            <label htmlFor="mobile">Téléphone mobile</label>
                            <input type="text" name="mobile" value={newAdministrator?.mobile || ""}
                                   onChange={onChangeNewAdmin}/>
                        </div>
                        <div className="form-input">
                            <label htmlFor="email">Email</label>
                            <input type="email" name="email" value={newAdministrator?.email || ""}
                                   onChange={onChangeNewAdmin}/>
                        </div>
                        <div className="form-input">
                            <label htmlFor="familyAffiliation" className="required">Lien de parenté</label>
                            <select ref={affiliationRef} name="familyAffiliation" required onChange={onChangeNewAdmin} value={newAdministrator?.familyAffiliation || ""}>
                                <option value="" disabled>Sélectionner un lien de parenté</option>
                                {newAdministrator?.civility === "M" ?
                                    (affiliationList['maleAffiliation']).map((affiliation, index) => (
                                        <option key={index} defaultValue={adminInfos?.familyAffiliation == index + 1}
                                                name="familyAffiliation" value={index + 1}>{affiliation}</option>))
                                    : (affiliationList['femaleAffiliation']).map((affiliation, index) => (
                                            <option key={index} defaultValue={adminInfos?.familyAffiliation == index + 1}
                                                    name="familyAffiliation" value={index + 1}>{affiliation}</option>))
                                }
                            </select>
                        </div>
                    </>
                }
                <div className="errors"></div>
            </fieldset>
            {!noSubmitButton && (
                <div className="buttons-container">
                    <button
                        type="button"
                        className="green-button"
                        onClick={handleSubmit}
                    >
                        <i className="fa fa-check"></i>
                        <span>Enregistrer les modifications</span>
                    </button>
                </div>
            )}
        </FormWrapper>
    )
}
